import React, { useState, useEffect, useContext, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';
import { AuthContext } from '../../Header/auth - context';

const readFileAsDataUrl = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
});

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + 'Z';
};

const emptyForm = { tekst: '', alarm: false, rok: null, id_status: null };

// Deljeni dialog za unos/izmenu beleške — koristi ga i Unos i Kalendar tab.
const BeleznicaDialog = ({ visible, editId, statusiDB, onHide, onSaved, toast }) => {
    const auth = useContext(AuthContext);
    const fileInputRef = useRef(null);

    const [form,          setForm]          = useState(emptyForm);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [slike,         setSlike]         = useState([]);
    const [pendingSlike,  setPendingSlike]  = useState([]);
    const [lightbox,      setLightbox]      = useState(null);

    const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token });
    const handleErrors = r => { if (r.ok) return r.json(); throw r; };
    const call = (body) => fetch(process.env.REACT_APP_BACKEND_URL + '/beleznica/manage', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
    }).then(handleErrors);

    const defaultStatus = () => {
        const akt = statusiDB.find(s => String(s.label).toLowerCase().startsWith('aktiv'));
        return akt ? akt.value : (statusiDB[0]?.value ?? null);
    };

    useEffect(() => {
        if (!visible) return;
        setFormSubmitted(false);
        setPendingSlike([]);
        if (!editId) {
            setForm({ ...emptyForm, id_status: defaultStatus() });
            setSlike([]);
            return;
        }
        // edit — učitaj belešku + slike
        call({ id: editId, step: 'GET' })
            .then(data => {
                const row = data[0] || {};
                setForm({
                    tekst:     row.tekst || '',
                    alarm:     !!row.alarm,
                    rok:       row.rok ? new Date(row.rok) : null,
                    id_status: row.id_status || null,
                });
            }).catch(() => {});
        fetch(process.env.REACT_APP_BACKEND_URL + '/beleznica/getSlike', {
            method: 'POST', headers: authHeaders(), body: JSON.stringify({ beleznica_id: editId }),
        }).then(handleErrors).then(setSlike).catch(() => setSlike([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, editId]);

    const uploadPending = async (beleznica_id) => {
        for (const s of pendingSlike) {
            await fetch(process.env.REACT_APP_BACKEND_URL + '/beleznica/addSlika', {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ beleznica_id, slika: s.data, filename: s.filename }),
            }).catch(() => {});
        }
    };

    const handleSave = async () => {
        setFormSubmitted(true);
        if (!form.tekst || !form.tekst.trim()) {
            toast?.current?.show({ severity: 'warn', summary: 'Upozorenje', detail: 'Unesite tekst beleške!', life: 3000 });
            return;
        }
        if (form.alarm && !form.rok) {
            toast?.current?.show({ severity: 'warn', summary: 'Upozorenje', detail: 'Za alarm morate uneti rok (datum i vreme)!', life: 3000 });
            return;
        }
        const payload = { tekst: form.tekst, alarm: form.alarm ? 1 : 0, rok: toUtcIso(form.rok), id_status: form.id_status };
        try {
            const data = await call({ id: editId, step: editId ? 'UPDATE' : 'INSERT', formData: payload });
            const msg = data[0]?.message;
            const newId = data[0]?.ID || editId;
            if (msg && msg !== 'Nema izmena u podacima.' && !String(msg).toLowerCase().includes('uspešn')) {
                toast?.current?.show({ severity: 'error', summary: 'Greška', detail: msg, life: 4000 });
                return;
            }
            if (newId && pendingSlike.length) await uploadPending(newId);
            toast?.current?.show({ severity: 'success', summary: editId ? 'Sačuvano!' : 'Dodato!', life: 2500 });
            onSaved && onSaved();
            onHide();
        } catch (err) {
            if (err.json) err.json().then(e => toast?.current?.show({ severity: 'error', summary: 'Greška', detail: e.message, life: 4000 }));
            else toast?.current?.show({ severity: 'error', summary: 'Greška', detail: 'Neuspešno čuvanje.', life: 3000 });
        }
    };

    const handleDelete = () => {
        if (!editId) return;
        confirmDialog({
            message: 'Obrisati ovu belešku?',
            header: 'Potvrda', icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Da', rejectLabel: 'Ne', acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const data = await call({ id: editId, step: 'DELETE' });
                    toast?.current?.show({ severity: 'success', summary: data[0]?.message || 'Obrisano!', life: 2500 });
                    onSaved && onSaved();
                    onHide();
                } catch {
                    toast?.current?.show({ severity: 'error', summary: 'Greška pri brisanju.', life: 3000 });
                }
            },
        });
    };

    const handlePaste = async (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                const data = await readFileAsDataUrl(file);
                setPendingSlike(prev => [...prev, { data, filename: file.name || 'paste.png' }]);
            }
        }
    };

    const handleFileSelect = async (e) => {
        for (const file of Array.from(e.target.files)) {
            const data = await readFileAsDataUrl(file);
            setPendingSlike(prev => [...prev, { data, filename: file.name }]);
        }
        e.target.value = '';
    };

    const deleteExistingSlika = (id) => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/beleznica/deleteSlika', {
            method: 'POST', headers: authHeaders(), body: JSON.stringify({ id }),
        }).then(handleErrors).then(() => setSlike(prev => prev.filter(s => s.id !== id))).catch(() => {});
    };

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span>{editId && <Button label="Obriši" icon="pi pi-trash" className="p-button-danger p-button-text" onClick={handleDelete} />}</span>
            <span style={{ display: 'flex', gap: '8px' }}>
                <Button label="Otkaži" icon="pi pi-times" className="p-button-secondary" onClick={onHide} />
                <Button label="Sačuvaj" icon="pi pi-check" className="p-button-success" onClick={handleSave} />
            </span>
        </div>
    );

    return (
        <>
            <Dialog
                visible={visible}
                onHide={onHide}
                header={editId ? 'Izmena beleške' : 'Nova beleška'}
                footer={footer}
                style={{ width: '46vw' }}
                breakpoints={{ '960px': '90vw' }}
                modal draggable={false} resizable={false}
            >
                <div className="bel-form">
                    <label>Tekst:</label>
                    <InputTextarea
                        value={form.tekst}
                        onChange={e => setForm(p => ({ ...p, tekst: e.target.value }))}
                        onPaste={handlePaste}
                        rows={5} autoResize style={{ width: '100%' }}
                        className={formSubmitted && !form.tekst.trim() ? 'p-invalid' : ''}
                        placeholder="Unesite belešku (Ctrl+V za sliku)" />

                    <div className="bel-form-row">
                        <div className="bel-form-col">
                            <label>Status:</label>
                            <Dropdown value={form.id_status} options={statusiDB} optionLabel="label" optionValue="value"
                                onChange={e => setForm(p => ({ ...p, id_status: e.value }))} placeholder="Status" style={{ width: '100%' }} />
                        </div>
                        <div className="bel-form-col">
                            <label>Rok (datum i vreme) — opciono:</label>
                            <Calendar value={form.rok}
                                onChange={e => setForm(p => ({ ...p, rok: e.value, alarm: e.value ? p.alarm : false }))}
                                dateFormat="dd.mm.yy" showTime hourFormat="24" showButtonBar
                                mask="99.99.9999 99:99" style={{ width: '100%' }} />
                        </div>
                    </div>

                    <div className="bel-alarm-row">
                        <Checkbox inputId="belAlarm" checked={form.alarm} disabled={!form.rok}
                            onChange={e => setForm(p => ({ ...p, alarm: e.checked }))} />
                        <label htmlFor="belAlarm" style={{ opacity: form.rok ? 1 : 0.5 }}>
                            Alarm — pošalji mail 1h pre roka{!form.rok && ' (izaberite rok)'}
                        </label>
                    </div>

                    <div className="bel-slike">
                        {slike.map(s => (
                            <div key={s.id} className="bel-slika-wrap">
                                <img src={s.slika} alt={s.filename || 'slika'} className="bel-slika-thumb" onClick={() => setLightbox(s.slika)} />
                                <button className="bel-slika-del" onClick={() => deleteExistingSlika(s.id)}>×</button>
                            </div>
                        ))}
                        {pendingSlike.map((s, i) => (
                            <div key={`p${i}`} className="bel-slika-wrap bel-slika-pending">
                                <img src={s.data} alt={s.filename} className="bel-slika-thumb" onClick={() => setLightbox(s.data)} />
                                <button className="bel-slika-del" onClick={() => setPendingSlike(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                            </div>
                        ))}
                        <div className="bel-slika-add">
                            <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileSelect} />
                            <button className="bel-slika-add-btn" onClick={() => fileInputRef.current?.click()} title="Dodaj sliku">
                                <i className="pi pi-image" /> Dodaj sliku
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>

            {lightbox && (
                <div className="bel-lightbox" onClick={() => setLightbox(null)}>
                    <button className="bel-lightbox-close" onClick={() => setLightbox(null)}>×</button>
                    <img src={lightbox} alt="slika" onClick={e => e.stopPropagation()} />
                </div>
            )}
        </>
    );
};

export default BeleznicaDialog;
